import React, { createContext, useEffect, useState, ReactNode } from 'react';
import {
  CartState,
  CartLine,
  Merchandise,
  CartLineAttributes,
  DiscountAllocation,
  CartProps,
  Payload,
  AjaxLine,
  OptionWithValue,
  CartData,
} from '@interfaces/cart';
import InfoPopup from '@components/molecules/InfoPopup';
import { formatAmount } from '@helpers/global';

const currencyCode = window?.DEPT?.localization?.currencyCode || 'USD';

const defaultCartState: CartState = {
  id: null,
  subtotal: {
    amount: '0',
    currencyCode,
  },
  lines: [],
  totalQuantity: 0,
  cost: null,
  totalDiscount: {
    amount: '0',
    currencyCode,
  },
};

interface CartContextProps {
  children: ReactNode;
  data: CartData;
}

const CartContext = createContext<CartProps | undefined>(undefined);

const CartContextProvider: React.FC<CartContextProps> = ({ children, data }) => {
  const [cartState, setCartState] = useState<CartState>(defaultCartState);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [popupMessage, setPopupMessage] = useState<string | null>(null);
  const [popupSubMessage, setPopupSubMessage] = useState<string | null>(null);

  const openCartEvent = new CustomEvent('cart:open');

  const closePopup = () => {
    setPopupMessage(null);
    setPopupSubMessage(null);
  };

  const fetchCart = async (triggerEvent = true) => {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart.');
      }

      const updatedCart = await response.json();

      setCartState({
        id: updatedCart.token,
        lines: updatedCart.items.map(formatAjaxCartLine),
        totalQuantity: updatedCart.item_count,
        subtotal: {
          amount: formatAmount(updatedCart.items_subtotal_price),
          currencyCode,
        },
        cost: {
          subtotalAmount: {
            amount: formatAmount(updatedCart.items_subtotal_price),
            currencyCode,
          },
          totalAmount: {
            amount: formatAmount(updatedCart.total_price),
            currencyCode,
          },
        },
        totalDiscount: {
          amount: formatAmount(updatedCart.total_discount),
          currencyCode,
        },
      });

      if (triggerEvent) {
        document.dispatchEvent(openCartEvent);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const formatAjaxCartLine = (ajaxLine: AjaxLine): CartLine => ({
    id: `gid://shopify/CartLine/${ajaxLine.key}`,
    key: ajaxLine.key,
    quantity: ajaxLine.quantity,
    attributes: Object.entries(ajaxLine.properties || {}).map(
      ([key, value]): CartLineAttributes => ({
        key,
        value: String(value),
      })
    ),
    cost: {
      compareAtAmountPerQuantity: {
        amount: formatAmount(ajaxLine.original_line_price),
        currencyCode,
      },
      totalAmount: {
        amount: formatAmount(ajaxLine.line_price),
        currencyCode,
      },
    },
    merchandise: {
      id: `gid://shopify/ProductVariant/${ajaxLine.variant_id}`,
      sku: ajaxLine.sku,
      quantityAvailable: ajaxLine.quantity_rule?.max || Infinity,
      selectedOptions: ajaxLine.options_with_values.map(
        (opt: OptionWithValue): Merchandise['selectedOptions'][number] => ({
          name: opt.name,
          value: opt.value,
        })
      ),
      price: {
        amount: formatAmount(ajaxLine.price),
        currencyCode,
      },
      image: {
        width: ajaxLine.featured_image.width,
        height: ajaxLine.featured_image.height,
        url: ajaxLine.featured_image.url,
        altText: ajaxLine.featured_image.alt || null,
      },
      product: {
        id: `gid://shopify/Product/${ajaxLine.product_id}`,
        vendor: ajaxLine.vendor,
        type: ajaxLine.product_type,
        title: ajaxLine.product_title,
        handle: ajaxLine.handle,
      },
      discounts: ajaxLine.discounts,
    },
    discountAllocations: ajaxLine.line_level_discount_allocations.map(
      (alloc): DiscountAllocation => ({
        discountedAmount: {
          amount: formatAmount(alloc.amount),
          currencyCode,
        },
      })
    ),
    lineLevelTotalDiscount: {
      amount: formatAmount(ajaxLine.line_level_total_discount),
      currencyCode,
    }
  });

  const cart = {
    add: async (payload: Payload[]) => {
      try {
        const validPayload = payload.filter(item => item.id); // Filter out items without an id
        const requests = validPayload.map(item => {
          const body = {
            id: item.id,
            quantity: item.quantity,
            properties: {
              ...item.attributes?.reduce((acc, attr) => {
                acc[attr.key] = attr.value;
                return acc;
              }, {} as Record<string, string>),
              ...(item.relatedVariants && { __internal_relatedVariants: item.relatedVariants }),
            },
          };

          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
        });

        // Execute all requests in parallel and check for errors
        const responses = await Promise.all(requests);
        const failedResponses = responses.filter(response => !response.ok);

        if (failedResponses.length > 0) {
          throw new Error(`Failed to add some items to cart: ${failedResponses.length} errors.`);
        }

        await fetchCart();
      } catch (error) {
        console.error('Error adding items to cart:', error);
      }
    },

    remove: async (payload: (string | number)[]) => {
      try {
        for (const key of payload) {
          const numericKey = String(key).split('/').pop();

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: numericKey,
              quantity: 0,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to remove item ${numericKey} from cart.`);
          }
        }

        await fetchCart();
      } catch (error) {
        console.error('Error removing items from cart:', error);
      }
    },

    update: async (payload: Payload[]) => {
      try {
        const validPayload = payload.filter(item => item.id); // Filter out invalid items
  
        for (const item of validPayload) {
          const numericKey = String(item.id).split('/').pop();
  
          const body = {
            id: numericKey,
            quantity: item.quantity,
            ...(item.attributes && {
              properties: item.attributes.reduce((acc, attr) => {
                acc[attr.key] = attr.value;
                return acc;
              }, {} as Record<string, string>),
            }),
          };
  
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          });
  
          if (!response.ok) {
            throw new Error(`Failed to update item ${numericKey} in cart.`);
          }
        }
  
        await fetchCart();
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    },

    share: async () => {
      if (!cartState.lines || cartState.lines.length === 0) {
        console.error("Cart is empty. Cannot share an empty cart.");
        return;
      }

      try {
        const cartData = {
          items: cartState.lines.map((line) => ({
            id: line.merchandise.id.split('/').pop(),
            quantity: line.quantity,
          })),
        };

        const shareLink = `${window.location.origin}/cart?sharedCart=${encodeURIComponent(
          JSON.stringify(cartData)
        )}`;

        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareLink);
          setPopupMessage(data.shareLinkCopiedMsg);
          setPopupSubMessage(data.shareFriendsMsg)
        } else {
          console.error('Clipboard API not supported.');
        }
      } catch (error) {
        console.error('Error sharing cart:', error);
      }
    },

    loadSharedCart: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedCartId = urlParams.get('sharedCart');
    
      if (sharedCartId) {
        try {
          const decodedCartData = JSON.parse(decodeURIComponent(sharedCartId));
    
          // Step 1: Clear the current cart
          if (cartState.lines && cartState.lines.length > 0) {
            // Remove all existing items by setting their quantity to 0
            await Promise.all(
              cartState.lines.map(line =>
                cart.update([{ id: line.id, quantity: 0 }])
              )
            );
          }
    
          // Step 2: Add shared cart items
          if (decodedCartData.items && decodedCartData.items.length > 0) {
            await cart.add(
              decodedCartData.items.map((item: any) => ({
                id: item.id,
                quantity: item.quantity,
              }))
            );
          }
    
          // Remove the sharedCart parameter from the URL
          const url = new URL(window.location.href);
          url.searchParams.delete('sharedCart');
          window.history.replaceState({}, document.title, url.toString());
    
          setPopupMessage(data.sharedCartSuccessMsg);
        } catch (error) {
          console.error('Error loading shared cart:', error);
          setPopupMessage('Failed to load shared cart.');
        }
      }
    },

    state: cartState,
  };

  useEffect(() => {
    const initializeCart = async () => {
      try {
        await fetchCart(false);
        await cart.loadSharedCart();
      } catch (error) {
        console.error('Error initializing cart:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    initializeCart();
  }, []);

  useEffect(() => {
    const cartCountEls = document.querySelectorAll('[data-cart-count]');
    if (cartCountEls.length > 0) {
      cartCountEls.forEach(cartCountEl => {
        cartCountEl.textContent = cartState.totalQuantity?.toString() || '0';
      });
    }
    
    const handleExternalAddToCart: EventListener = (e: Event) => {
      if (e instanceof CustomEvent) {
        cart.add(e.detail.payload);
      }
    };

    document.addEventListener('cart:add', handleExternalAddToCart);
    return () => document.removeEventListener('cart:add', handleExternalAddToCart);
  }, [cart]);

  return (
    <>
      <CartContext.Provider value={{ data, cart, loading: isInitialLoad }}>
        {children}
      </CartContext.Provider>
      {popupMessage && <InfoPopup message={popupMessage} subMessage={popupSubMessage} onClose={closePopup} />}
    </>
  );
};

export const useCartContext = () => {
  const context = React.useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within a CartContextProvider');
  }
  return context;
};

export default CartContextProvider;
